import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Heart, Upload, ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { generateSlug } from '../../lib/utils/slugUtils';
import type { Recipe, RecipeFormData } from '../../types/recipes';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

interface CreateRecipeModalProps {
  onClose: () => void;
  onSuccess: () => void;
  recipe?: Recipe;
}

const RECIPE_DRAFT_KEY = 'recipe_create_draft_v1';

const FEELING_TAGS = [
  { value: 'energizing', labelKey: 'energizing' },
  { value: 'comforting', labelKey: 'comforting' },
  { value: 'gut_health', labelKey: 'gutHealth' },
  { value: 'quick_easy', labelKey: 'quickEasy' },
  { value: 'celebration', labelKey: 'celebration' }
];

const CUISINE_TYPES = [
  { value: 'italian', labelKey: 'italian' },
  { value: 'mexican', labelKey: 'mexican' },
  { value: 'indian', labelKey: 'indian' },
  { value: 'chinese', labelKey: 'chinese' },
  { value: 'japanese', labelKey: 'japanese' },
  { value: 'thai', labelKey: 'thai' },
  { value: 'mediterranean', labelKey: 'mediterranean' },
  { value: 'french', labelKey: 'french' },
  { value: 'american', labelKey: 'american' },
  { value: 'middle_eastern', labelKey: 'middleEastern' },
  { value: 'other', labelKey: 'other' }
];

const MEAL_TYPES = [
  { value: 'breakfast', labelKey: 'breakfast' },
  { value: 'lunch', labelKey: 'lunch' },
  { value: 'dinner', labelKey: 'dinner' },
  { value: 'snack', labelKey: 'snack' },
  { value: 'apero', labelKey: 'apero' },
  { value: 'drink', labelKey: 'drink' },
  { value: 'other', labelKey: 'other' }
];

const DIETARY_PREFERENCES = [
  { value: 'vegetarian', labelKey: 'vegetarian' },
  { value: 'vegan', labelKey: 'vegan' },
  { value: 'gluten_free', labelKey: 'glutenFree' },
  { value: 'dairy_free', labelKey: 'dairyFree' },
  { value: 'keto', labelKey: 'keto' },
  { value: 'paleo', labelKey: 'paleo' },
  { value: 'low_carb', labelKey: 'lowCarb' },
  { value: 'nut_free', labelKey: 'nutFree' }
];

const COMMON_INGREDIENTS = [
  'Flour (All Purpose)',
  'Flour (Whole Wheat)',
  'Sugar',
  'Salt',
  'Pepper',
  'Olive Oil',
  'Butter',
  'Eggs',
  'Milk',
  'Garlic',
  'Onion',
  'Tomatoes',
  'Chicken',
  'Beef',
  'Rice',
  'Pasta',
  'Cheese',
  'Herbs',
  'Spices'
];

interface IngredientItem {
  amount: string;
  ingredient: string;
}

interface InstructionStep {
  text: string;
  tip: string;
}

export function CreateRecipeModal({ onClose, onSuccess, recipe }: CreateRecipeModalProps) {
  const isEditing = !!recipe;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<RecipeFormData>(recipe ? {
    title: recipe.title || '',
    description: recipe.description || '',
    ingredients: recipe.ingredients || [],
    instructions: recipe.instructions || [],
    prep_time: recipe.prep_time?.toString() || '',
    cook_time: recipe.cook_time?.toString() || '',
    servings: recipe.servings?.toString() || '',
    cuisine_type: recipe.cuisine_type || '',
    meal_type: recipe.meal_type || '',
    dietary_preferences: recipe.dietary_preferences || []
  } : {
    title: '',
    description: '',
    ingredients: [],
    instructions: [],
    prep_time: '',
    cook_time: '',
    servings: '',
    cuisine_type: '',
    meal_type: '',
    dietary_preferences: []
  });

  // Initialize ingredient list from recipe if editing
  const [ingredientList, setIngredientList] = useState<IngredientItem[]>(
    recipe?.ingredients?.map(ing => ({ amount: '', ingredient: ing })) || [{ amount: '', ingredient: '' }]
  );

  // Initialize instruction steps from recipe if editing
  const [instructionSteps, setInstructionSteps] = useState<InstructionStep[]>(
    recipe?.instructions?.map(inst => ({ text: inst, tip: '' })) || [{ text: '', tip: '' }]
  );

  const [selectedFeelings, setSelectedFeelings] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(recipe?.image_url || null);
  const [ingredientSuggestions, setIngredientSuggestions] = useState<string[]>([]);
  const [activeIngredientIndex, setActiveIngredientIndex] = useState<number | null>(null);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saved'>('idle');
  const titleRef = useRef<HTMLInputElement>(null);
  const modalTitleId = 'recipe-modal-title';

  const parsedIngredientCount = useMemo(
    () => ingredientList.filter(item => item.ingredient.trim() !== '').length,
    [ingredientList]
  );

  const closeModal = () => {
    onClose();
  };

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (isEditing) return;
    try {
      const raw = localStorage.getItem(RECIPE_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as {
        currentStep?: number;
        formData?: RecipeFormData;
        ingredientList?: IngredientItem[];
        instructionSteps?: InstructionStep[];
        selectedFeelings?: string[];
      };
      if (draft.formData) setFormData(draft.formData);
      if (draft.ingredientList?.length) setIngredientList(draft.ingredientList);
      if (draft.instructionSteps?.length) setInstructionSteps(draft.instructionSteps);
      if (draft.selectedFeelings) setSelectedFeelings(draft.selectedFeelings);
      if (draft.currentStep && draft.currentStep >= 1 && draft.currentStep <= 4) {
        setCurrentStep(draft.currentStep);
      }
    } catch {
      // ignore invalid drafts
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing || showSuccess) return;
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(RECIPE_DRAFT_KEY, JSON.stringify({
          currentStep,
          formData,
          ingredientList,
          instructionSteps,
          selectedFeelings,
        }));
        setSaveState('saved');
        window.setTimeout(() => setSaveState('idle'), 1200);
      } catch {
        // ignore storage errors
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [currentStep, formData, ingredientList, instructionSteps, selectedFeelings, isEditing, showSuccess]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIngredientInput = (index: number, field: 'amount' | 'ingredient', value: string) => {
    const updated = [...ingredientList];
    updated[index] = { ...updated[index], [field]: value };
    setIngredientList(updated);

    // Show suggestions for ingredient field
    if (field === 'ingredient' && value.length > 2) {
      const filtered = COMMON_INGREDIENTS.filter(ing => 
        ing.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setIngredientSuggestions(filtered);
      setActiveIngredientIndex(index);
    } else if (field === 'ingredient' && value.length <= 2) {
      setIngredientSuggestions([]);
    }
  };

  const selectIngredientSuggestion = (suggestion: string) => {
    if (activeIngredientIndex !== null) {
      const updated = [...ingredientList];
      updated[activeIngredientIndex] = { 
        ...updated[activeIngredientIndex], 
        ingredient: suggestion,
      };
      setIngredientList(updated);
      setIngredientSuggestions([]);
      setActiveIngredientIndex(null);
    }
  };

  const addIngredientLine = () => {
    setIngredientList([...ingredientList, { amount: '', ingredient: '' }]);
  };

  const removeIngredient = (index: number) => {
    if (ingredientList.length > 1) {
      setIngredientList(ingredientList.filter((_, i) => i !== index));
    }
  };

  const parsePastedIngredients = () => {
    const lines = pasteText.split('\n').filter(line => line.trim());
    const parsed: IngredientItem[] = lines.map(line => {
      // Try to parse "amount ingredient" or just "ingredient"
      const parts = line.trim().split(/\s+/);
      if (parts.length >= 2 && /^\d+/.test(parts[0])) {
        return {
          amount: parts[0],
          ingredient: parts.slice(1).join(' '),
        };
      }
      return { amount: '', ingredient: line.trim() };
    });
    setIngredientList(parsed.length > 0 ? parsed : [{ amount: '', ingredient: '' }]);
    setPasteMode(false);
    setPasteText('');
  };

  const addInstructionStep = () => {
    setInstructionSteps([...instructionSteps, { text: '', tip: '' }]);
  };

  const updateInstructionStep = (index: number, field: 'text' | 'tip', value: string) => {
    const updated = [...instructionSteps];
    updated[index] = { ...updated[index], [field]: value };
    setInstructionSteps(updated);
  };

  const removeInstructionStep = (index: number) => {
    if (instructionSteps.length > 1) {
      setInstructionSteps(instructionSteps.filter((_, i) => i !== index));
    }
  };

  const toggleFeeling = (feeling: string) => {
    setSelectedFeelings(prev => 
      prev.includes(feeling) 
        ? prev.filter(f => f !== feeling)
        : [...prev, feeling]
    );
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.title.trim() !== '' && formData.description.trim() !== '' && formData.cuisine_type !== '' && (formData.meal_type ?? '') !== '';
      case 2:
        return ingredientList.some(item => item.ingredient.trim() !== '');
      case 3:
        return instructionSteps.some(step => step.text.trim() !== '');
      case 4:
        return formData.prep_time !== '' && formData.cook_time !== '';
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canProceedToNextStep() && currentStep < 4) {
      const next = currentStep + 1;
      setCurrentStep(next);
      window.dispatchEvent(new CustomEvent('recipe-flow-step', { detail: { step: next, mode: isEditing ? 'edit' : 'create' } }));
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const previous = currentStep - 1;
      setCurrentStep(previous);
      window.dispatchEvent(new CustomEvent('recipe-flow-step', { detail: { step: previous, mode: isEditing ? 'edit' : 'create' } }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t('forms.recipe.signInToShare'));

      const ingredients = ingredientList
        .filter(item => item.ingredient.trim() !== '')
        .map(item => {
          if (item.amount.trim()) {
            return `${item.amount} ${item.ingredient}`;
          }
          return item.ingredient;
        });

      // Convert instruction steps to string array
      const instructions = instructionSteps
        .filter(step => step.text.trim() !== '')
        .map((step, index) => {
          if (step.tip.trim()) {
            return `${step.text} (Tip: ${step.tip})`;
          }
          return step.text;
        });

      // Upload image if provided
      let imageUrl = recipe?.image_url || '';
      if (imageFile) {
        const maxFileSize = 5 * 1024 * 1024;
        if (!imageFile.type.startsWith('image/')) {
          throw new Error(t('forms.recipe.uploadImageFileError'));
        }
        if (imageFile.size > maxFileSize) {
          throw new Error(t('forms.recipe.imageTooLargeError'));
        }

        const { data: imageData, error: imageError } = await supabase.storage
          .from('recipe-images')
          .upload(`${user.id}/${Date.now()}-${imageFile.name}`, imageFile);

        if (imageError) throw imageError;

        const { data: { publicUrl } } = supabase.storage
          .from('recipe-images')
          .getPublicUrl(imageData.path);

        imageUrl = publicUrl;
      }

      // Validate required fields
      if (!formData.cuisine_type) {
        throw new Error(t('forms.recipe.selectCuisineType'));
      }

      if (isEditing && recipe) {
        // Update existing recipe
        const { error: updateError } = await supabase
          .from('recipes')
          .update({
            title: formData.title,
            description: formData.description,
            ingredients: ingredients,
            instructions: instructions,
            prep_time: parseInt(formData.prep_time) || 0,
            cook_time: parseInt(formData.cook_time) || 0,
            servings: parseInt(formData.servings) || 1,
            cuisine_type: formData.cuisine_type,
            meal_type: formData.meal_type || null,
            dietary_preferences: Array.from(new Set([...formData.dietary_preferences, ...selectedFeelings])),
            image_url: imageUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', recipe.id);

        if (updateError) throw updateError;
      } else {
        // Create new recipe
        const { error: insertError } = await supabase
          .from('recipes')
          .insert([{
            user_id: user.id,
            title: formData.title,
            slug: generateSlug(formData.title),
            description: formData.description,
            ingredients: ingredients,
            instructions: instructions,
            prep_time: parseInt(formData.prep_time) || 0,
            cook_time: parseInt(formData.cook_time) || 0,
            servings: parseInt(formData.servings) || 1,
            difficulty: 'easy',
            cuisine_type: formData.cuisine_type,
            meal_type: formData.meal_type || null,
            dietary_preferences: Array.from(new Set([...formData.dietary_preferences, ...selectedFeelings])),
            image_url: imageUrl
          }]);

        if (insertError) throw insertError;
      }
      
      setShowSuccess(true);
      window.dispatchEvent(new CustomEvent('recipe-flow-submitted', {
        detail: { mode: isEditing ? 'edit' : 'create', ingredientCount: ingredients.length, instructionCount: instructions.length }
      }));
      try {
        localStorage.removeItem(RECIPE_DRAFT_KEY);
      } catch {
        // ignore storage errors
      }
      setTimeout(() => {
        onSuccess();
      }, 3000);
    } catch (err: any) {
      const message = typeof err?.message === 'string' ? err.message : t('forms.recipe.genericError');
      if (message.toLowerCase().includes('duplicate key') || message.toLowerCase().includes('recipes_slug_key')) {
        setError(t('forms.recipe.duplicateTitleError'));
      } else {
        setError(message);
      }
      setLoading(false);
    }
  };

  const handleSuccessAction = async () => {
    if (isEditing) {
      onSuccess();
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      onSuccess();
      return;
    }

    onSuccess();
    navigate(`/profile/${user.id}/recipes`);
  };

  if (showSuccess) {
    const successContent = (
      <div className="fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[999999] p-4">
        <div className="bg-background rounded-xl p-8 w-full max-w-md text-center">
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <Sparkles className="w-16 h-16 text-accent-text animate-pulse" />
              <Heart className="w-8 h-8 text-red-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-content mb-4">{t('forms.recipe.thankYou')}</h2>
          <p className="text-content/70 mb-6">
            {isEditing
              ? t('forms.recipe.recipeUpdatedSaved')
              : t('forms.recipe.recipePendingReview')}
          </p>
          <button
            onClick={handleSuccessAction}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors"
          >
            {isEditing ? t('forms.recipe.done') : t('forms.recipe.goToMyRecipes')}
          </button>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(successContent, document.body) : successContent;
  }

  const content = (
    <div className="fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[999999] p-0 md:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        className="bg-background rounded-none md:rounded-xl w-full h-full min-h-[100dvh] md:min-h-0 md:h-auto md:max-w-2xl md:max-h-[90vh] overflow-y-auto flex flex-col md:shadow-2xl"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 id={modalTitleId} className="text-2xl font-semibold text-content">
              {currentStep === 1 && t('forms.recipe.whatCooking')}
              {currentStep === 2 && t('forms.recipe.ingredients')}
              {currentStep === 3 && t('forms.recipe.method')}
              {currentStep === 4 && t('forms.recipe.vibe')}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStep, total: 4 })}</p>
            {!isEditing && (
              <p className="text-xs text-content/50 mt-1" aria-live="polite">
                {saveState === 'saved' ? t('forms.recipe.draftSaved') : t('forms.recipe.draftAutosave')}
              </p>
            )}
          </div>
          <button onClick={closeModal} aria-label={t('forms.recipe.closeRecipeForm')} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((step) => (
              <button
                key={step}
                type="button"
                onClick={() => step <= currentStep && setCurrentStep(step)}
                aria-label={`Go to step ${step}`}
                disabled={step > currentStep}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  step <= currentStep ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div aria-live="assertive" className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
          )}

          {/* Step 1: The Story */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Cuisine Type Tags */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-3">
                    {t('forms.recipe.cuisineType')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CUISINE_TYPES.map((cuisine) => (
                      <button
                        key={cuisine.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, cuisine_type: cuisine.value }))}
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.cuisine_type === cuisine.value
                            ? 'bg-accent-text text-white shadow-sm'
                            : 'bg-gray-100 text-content hover:bg-gray-200'
                        }`}
                      >
                        {formData.cuisine_type === cuisine.value && <Check className="w-4 h-4 mr-1.5" />}
                        {t(`forms.recipe.${cuisine.labelKey}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content mb-3">
                    {t('forms.recipe.mealType')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {MEAL_TYPES.map((meal) => (
                      <button
                        key={meal.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, meal_type: meal.value }))}
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.meal_type === meal.value
                            ? 'bg-accent-text text-white shadow-sm'
                            : 'bg-gray-100 text-content hover:bg-gray-200'
                        }`}
                      >
                        {formData.meal_type === meal.value && <Check className="w-4 h-4 mr-1.5" />}
                        {t(`forms.recipe.mealTypeOptions.${meal.labelKey}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content mb-3">
                    {t('forms.recipe.dietaryPreferences')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DIETARY_PREFERENCES.map((diet) => (
                      <button
                        key={diet.value}
                        type="button"
                        onClick={() => {
                          const current = formData.dietary_preferences;
                          const updated = current.includes(diet.value)
                            ? current.filter(d => d !== diet.value)
                            : [...current, diet.value];
                          setFormData(prev => ({ ...prev, dietary_preferences: updated }));
                        }}
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.dietary_preferences.includes(diet.value)
                            ? 'bg-accent-text text-white shadow-sm'
                            : 'bg-gray-100 text-content hover:bg-gray-200'
                        }`}
                      >
                        {formData.dietary_preferences.includes(diet.value) && <Check className="w-4 h-4 mr-1.5" />}
                        {t(`filters.${diet.labelKey}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">{t('forms.recipe.dishName')}</Label>
                <Input
                  ref={titleRef}
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('forms.recipe.dishPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('forms.recipe.whySpecial')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={5}
                  placeholder={t('forms.recipe.whySpecialPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-2">
                  {t('forms.recipe.showCreation')}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-accent-text transition-colors"
                >
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagePreview(null);
                          setImageFile(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-content/60">{t('forms.recipe.clickUpload')}</p>
                      <p className="text-sm text-content/40 mt-1">{t('forms.recipe.imageFormat')}</p>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: The Ingredients */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {!pasteMode ? (
                <>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-content/60">{t('forms.recipe.addIngredients')}</p>
                    <button
                      type="button"
                      onClick={() => setPasteMode(true)}
                      className="text-sm text-accent-text hover:text-accent-text/80 underline"
                    >
                      {t('forms.recipe.pasteFullList')}
                    </button>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs text-content/50">
                      {t('forms.recipe.includeAllIngredientsHint', { count: parsedIngredientCount })}
                    </p>
                    {ingredientList.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1 grid grid-cols-3 gap-3 relative">
                          <Input
                            type="text"
                            value={item.amount}
                            onChange={(e) => handleIngredientInput(index, 'amount', e.target.value)}
                            placeholder={t('forms.recipe.amount')}
                          />
                          <div className="col-span-2 relative">
                            <Input
                              type="text"
                              value={item.ingredient}
                              onChange={(e) => handleIngredientInput(index, 'ingredient', e.target.value)}
                              placeholder={t('forms.recipe.ingredient')}
                            />
                            {ingredientSuggestions.length > 0 && activeIngredientIndex === index && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                                {ingredientSuggestions.map((suggestion, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => selectIngredientSuggestion(suggestion)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {ingredientList.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeIngredient(index)}
                            className="flex-shrink-0 p-1 text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addIngredientLine}
                    className="w-full py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>+</span> {t('forms.common.addLine')}
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="paste_ingredients">{t('forms.recipe.pasteIngredients')}</Label>
                    <Textarea
                      id="paste_ingredients"
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      rows={8}
                      placeholder={t('forms.recipe.pastePlaceholder')}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={parsePastedIngredients}
                      className="flex-1 py-2 bg-accent-text text-white rounded-lg font-medium hover:bg-accent-text/90 transition-colors"
                    >
                      {t('forms.recipe.parseIngredients')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasteMode(false);
                        setPasteText('');
                      }}
                      className="flex-1 py-2 border border-gray-300 text-content rounded-lg font-medium hover:bg-gray-50 transition-colors"
                    >
                      {t('forms.common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: The Method */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <p className="text-sm text-content/60">{t('forms.recipe.addSteps')}</p>
              <div className="space-y-4">
                {instructionSteps.map((step, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-text text-white flex items-center justify-center font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-3">
                        <Textarea
                          value={step.text}
                          onChange={(e) => updateInstructionStep(index, 'text', e.target.value)}
                          rows={3}
                          placeholder={t('forms.recipe.stepPlaceholder', { index: index + 1 })}
                        />
                        <Input
                          type="text"
                          value={step.tip}
                          onChange={(e) => updateInstructionStep(index, 'tip', e.target.value)}
                          placeholder={`💡 ${t('forms.recipe.tipPlaceholder')}`}
                        />
                      </div>
                      {instructionSteps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInstructionStep(index)}
                          className="flex-shrink-0 p-1 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addInstructionStep}
                className="w-full py-3 border-2 border-dashed border-gray-300 text-content rounded-lg font-medium hover:border-accent-text hover:text-accent-text transition-colors flex items-center justify-center gap-2"
              >
                <span>+</span> {t('forms.recipe.addAnotherStep')}
              </button>
            </div>
          )}

          {/* Step 4: The Vibe */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-content mb-4">
                  {t('forms.recipe.howNourish')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {FEELING_TAGS.map((tag) => (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => toggleFeeling(tag.value)}
                      className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedFeelings.includes(tag.value)
                          ? 'bg-accent-text text-white shadow-sm'
                          : 'bg-gray-100 text-content hover:bg-gray-200'
                      }`}
                    >
                      {selectedFeelings.includes(tag.value) && <Check className="w-4 h-4 mr-1.5" />}
                      {t(`forms.recipe.${tag.labelKey}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prep_time">{t('forms.recipe.prepTime')}</Label>
                  <Input
                    id="prep_time"
                    type="number"
                    min="0"
                    value={formData.prep_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, prep_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cook_time">{t('forms.recipe.cookTime')}</Label>
                  <Input
                    id="cook_time"
                    type="number"
                    min="0"
                    value={formData.cook_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, cook_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="servings">{t('forms.recipe.servings')}</Label>
                  <Input
                    id="servings"
                    type="number"
                    min="1"
                    value={formData.servings}
                    onChange={(e) => setFormData(prev => ({ ...prev, servings: e.target.value }))}
                    placeholder="4"
                  />
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                canProceedToNextStep()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canProceedToNextStep()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !canProceedToNextStep()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? t('forms.common.sharing') : (
                <>
                  <Heart className="w-4 h-4" />
                  {t('forms.recipe.shareRecipe')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
